/**
 * Runtime for the structure blocks that act: trial spawners (waves of mobs,
 * then a reward, then a 30 minute cooldown), vaults (open with a trial key,
 * once) and the stronghold's silverfish spawner.
 *
 * DOM-free: the host (main.ts) gives it block access, the player and a mob
 * spawner. Spawners are found by scanning the loaded blocks round the player,
 * so nothing has to be registered at generation time.
 *
 * Deviations from Bedrock, on purpose: one fixed mob pool per spawner picked
 * from zombie / skeleton / spider (there is no breeze), difficulty scaling only
 * by player count (always 1 here), cooldowns persist as world flags,
 * and ominous vaults open with the ordinary trial key.
 */

import { AIR, SILVERFISH_SPAWNER_ID, TRIAL_SPAWNER_ID, VAULT_ID, VAULT_OPENED_ID } from '../blocks';
import { hash2, mulberry32 } from '../noise';
import { I } from './itemIds';
import { type LootKind, rollChestLoot } from './loot';
import { isEmpty, type Stack, stackOf } from './types';
import type { MobType } from './types';

/** 30 minutes at 20 ticks per second. */
export const TRIAL_COOLDOWN_TICKS = 30 * 60 * 20;
const ACTIVATE_RANGE = 14;
const TOTAL_MOBS = 6;
const SIMULTANEOUS = 2;
const SPAWN_EVERY = 40;
const POOLS: readonly MobType[] = ['zombie', 'skeleton', 'spider'];

export interface TrialHost {
  getBlock(x: number, y: number, z: number): number;
  setBlock(x: number, y: number, z: number, id: number): void;
  player(): { x: number; y: number; z: number; alive: boolean };
  /** Spawn a mob; the returned handle's `alive` says whether it still lives (null = could not spawn). */
  spawnMob(type: MobType, x: number, y: number, z: number): { alive: boolean } | null;
  /** Living mobs of a type within r blocks of a point (silverfish cap). */
  countNear(type: MobType, x: number, y: number, z: number, r: number): number;
  /** Throw a reward item out of a block. */
  eject(stack: Stack, x: number, y: number, z: number): void;
  rnd(): number;
  /** A spawner went on cooldown until this wall-clock time (ms): persist it. */
  saveCooldown?(key: string, expiresAtMs: number): void;
}

interface Wave { spawned: number; mobs: Array<{ alive: boolean }>; nextSpawn: number }

export class TrialRuntime {
  private tickN = 0;
  /** Spawner cells seen in the last scans, keyed 'x,y,z' -> block id. */
  private readonly known = new Map<string, number>();
  private readonly cooldownUntil = new Map<string, number>();
  private readonly waves = new Map<string, Wave>();
  private readonly silverDelay = new Map<string, number>();

  constructor(private readonly h: TrialHost) {}

  /** Forget everything (dimension switch / world load). */
  clear(): void { this.known.clear(); this.waves.clear(); this.silverDelay.clear(); }

  /** Is this trial spawner on cooldown or running a wave? */
  stateOf(x: number, y: number, z: number): 'idle' | 'active' | 'cooldown' {
    const key = `${x},${y},${z}`;
    if (this.waves.has(key)) return 'active';
    return (this.cooldownUntil.get(key) ?? 0) > this.tickN ? 'cooldown' : 'idle';
  }

  /** Cooldowns as remaining ticks, for saving. */
  serialize(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [k, t] of this.cooldownUntil) if (t > this.tickN) out[k] = t - this.tickN;
    return out;
  }
  restore(data: Record<string, number> | undefined): void {
    this.cooldownUntil.clear();
    for (const k in data ?? {}) this.cooldownUntil.set(k, this.tickN + data![k]);
  }

  /** Reload cooldowns saved as `trial_cd:<x,y,z>:<expiry ms>` world flags. */
  restoreFlags(flags: Iterable<string>, nowMs = Date.now()): number {
    let n = 0;
    for (const f of flags) {
      const m = /^trial_cd:(-?\d+,-?\d+,-?\d+):(\d+)$/.exec(f);
      if (!m) continue;
      const left = Math.ceil((Number(m[2]) - nowMs) / 50);
      if (left > 0) { this.cooldownUntil.set(m[1], this.tickN + left); n++; }
    }
    return n;
  }

  tick(): void {
    this.tickN++;
    const p = this.h.player();
    if (!p.alive) return;
    if (this.tickN % 20 === 1) this.scan(p);
    for (const [key, id] of this.known) {
      const [x, y, z] = key.split(',').map(Number);
      if (this.h.getBlock(x, y, z) !== id) { this.known.delete(key); this.waves.delete(key); continue; }
      if (id === TRIAL_SPAWNER_ID) this.tickTrial(key, x, y, z, p);
      else this.tickSilverfish(key, x, y, z, p);
    }
  }

  private scan(p: { x: number; y: number; z: number }): void {
    const px = Math.floor(p.x), py = Math.floor(p.y), pz = Math.floor(p.z);
    for (let y = py - 6; y <= py + 8; y++) {
      for (let z = pz - 16; z <= pz + 16; z++) {
        for (let x = px - 16; x <= px + 16; x++) {
          const id = this.h.getBlock(x, y, z);
          if (id === TRIAL_SPAWNER_ID || id === SILVERFISH_SPAWNER_ID) this.known.set(`${x},${y},${z}`, id);
        }
      }
    }
  }

  private near(x: number, y: number, z: number, p: { x: number; y: number; z: number }, r: number): boolean {
    const dx = x + 0.5 - p.x, dy = y + 0.5 - p.y, dz = z + 0.5 - p.z;
    return dx * dx + dy * dy + dz * dz <= r * r;
  }

  /** A free floor cell within `r` of the spawner: air above a solid-ish cell, two air high. */
  private spot(x: number, y: number, z: number, r: number): [number, number, number] | null {
    const h = this.h;
    for (let k = 0; k < 12; k++) {
      const sx = x + Math.floor((h.rnd() - 0.5) * 2 * r), sz = z + Math.floor((h.rnd() - 0.5) * 2 * r);
      for (let sy = y + 2; sy >= y - 2; sy--) {
        if (h.getBlock(sx, sy, sz) === AIR && h.getBlock(sx, sy + 1, sz) === AIR && h.getBlock(sx, sy - 1, sz) !== AIR) {
          return [sx + 0.5, sy, sz + 0.5];
        }
      }
    }
    return null;
  }

  private tickTrial(key: string, x: number, y: number, z: number, p: { x: number; y: number; z: number }): void {
    let w = this.waves.get(key);
    if (!w) {
      if ((this.cooldownUntil.get(key) ?? 0) > this.tickN || !this.near(x, y, z, p, ACTIVATE_RANGE)) return;
      w = { spawned: 0, mobs: [], nextSpawn: this.tickN + 20 };
      this.waves.set(key, w);
    }
    w.mobs = w.mobs.filter((m) => m.alive);
    if (w.spawned < TOTAL_MOBS && w.mobs.length < SIMULTANEOUS && this.tickN >= w.nextSpawn) {
      const at = this.spot(x, y, z, 4);
      w.nextSpawn = this.tickN + SPAWN_EVERY;
      const type = POOLS[Math.floor(hash2(x, z, 0x7a1) * POOLS.length)];
      const m = at && this.h.spawnMob(type, at[0], at[1], at[2]);
      if (m) { w.mobs.push(m); w.spawned++; }
    }
    if (w.spawned >= TOTAL_MOBS && w.mobs.length === 0) {
      this.waves.delete(key);
      this.cooldownUntil.set(key, this.tickN + TRIAL_COOLDOWN_TICKS);
      this.h.saveCooldown?.(key, Date.now() + TRIAL_COOLDOWN_TICKS * 50);
      this.reward(x, y, z);
    }
  }

  /** The wave's reward: a trial key (the usual prize) and a share of the chamber loot. */
  private reward(x: number, y: number, z: number): void {
    const h = this.h;
    h.eject(stackOf(I.TRIAL_KEY, 1), x + 0.5, y + 1.2, z + 0.5);
    const loot = rollChestLoot(mulberry32(Math.floor(hash2(x, z, this.tickN) * 4294967296)), 'trial_chambers');
    for (const s of loot.filter((a) => !isEmpty(a)).slice(0, 4)) h.eject(s, x + 0.5, y + 1.2, z + 0.5);
  }

  private tickSilverfish(key: string, x: number, y: number, z: number, p: { x: number; y: number; z: number }): void {
    if (!this.near(x, y, z, p, 16)) return;
    const wait = this.silverDelay.get(key) ?? 0;
    if (wait > this.tickN) return;
    this.silverDelay.set(key, this.tickN + 200 + Math.floor(this.h.rnd() * 600));
    if (this.h.countNear('silverfish', x, y, z, 9) >= 6) return;
    const n = 1 + Math.floor(this.h.rnd() * 4);
    for (let i = 0; i < n; i++) {
      const at = this.spot(x, y, z, 4);
      if (at) this.h.spawnMob('silverfish', at[0], at[1], at[2]);
    }
  }

  /**
   * Right-click on a vault with `heldId` in hand. 'opened' means the key is
   * spent (the caller consumes it) and the loot has been thrown out; the vault
   * is swapped for its spent form so it never pays twice.
   */
  useVault(x: number, y: number, z: number, heldId: number): 'opened' | 'no_key' | 'spent' {
    const id = this.h.getBlock(x, y, z);
    if (id === VAULT_OPENED_ID) return 'spent';
    if (id !== VAULT_ID || heldId !== I.TRIAL_KEY) return 'no_key';
    this.h.setBlock(x, y, z, VAULT_OPENED_ID);
    const kind: LootKind = hash2(x, z, 0x0a1) < 0.4 ? 'vault_ominous' : 'vault';
    const loot = rollChestLoot(mulberry32(Math.floor(hash2(x, y, z) * 4294967296)), kind);
    for (const s of loot) if (!isEmpty(s)) this.h.eject(s, x + 0.5, y + 1.1, z + 0.5);
    return 'opened';
  }
}
